import type { Stripe, StripeElements } from '@stripe/stripe-js';

import {
  PAYMENT_METHOD_VALUE,
  type PaymentMethodValue,
  type PaymentResult,
} from '@/features/payments';

import type { PaymentMethod } from './processingFee';

/** Fields the bank-transfer method collects. BECS requires a name and an email
 *  on the mandate; Stripe rejects the confirmation without them. */
export interface BankTransferDetails {
  name: string;
  email: string;
}

/** Everything a charge needs that is not derived from the row itself. */
export interface ChargeInput {
  method: PaymentMethod;
  selectedCard: string;
  cardholderName: string;
  saveCard: boolean;
  bankTransfer: BankTransferDetails;
}

/** Thrown where the message is already fit to show a director. */
export class PaymentError extends Error {}

/**
 * The `paymentMethod` string the API is sent.
 *
 * `to_delete_card` is the legacy name for a one-off token the backend charges
 * and then discards — which is what a new card is, unless the director asked to
 * keep it.
 */
export const paymentMethodValueOf = (
  method: PaymentMethod,
  saveCard: boolean,
): PaymentMethodValue => {
  if (method === 'bankTransfer') return PAYMENT_METHOD_VALUE.bankTransfer;
  if (method === 'savedCard' || saveCard) return PAYMENT_METHOD_VALUE.savedCard;
  return PAYMENT_METHOD_VALUE.singleUseCard;
};

/**
 * Turns a new card into a single-use token.
 *
 * Raw card details never reach this application — the iframe hands back a
 * token, and that is what the API is sent. Returns an empty string for methods
 * that have no card to tokenise, so the caller has one shape to pass on.
 */
export const tokeniseNewCard = async (
  stripe: Stripe,
  elements: StripeElements,
  cardholderName: string,
): Promise<string> => {
  const cardNumber = elements.getElement('cardNumber');
  if (cardNumber === null) {
    throw new PaymentError('The card form is not ready yet. Try again in a moment.');
  }

  const { token, error } = await stripe.createToken(cardNumber, {
    name: cardholderName.trim(),
  });

  if (token === undefined) {
    throw new PaymentError(
      error.message ?? 'That card could not be verified. Check the details.',
    );
  }

  return token.id;
};

/**
 * Confirms a BECS direct debit in the browser.
 *
 * Only reached when the backend did not settle the charge itself. The legacy
 * portal had this path commented out and relied entirely on the server having
 * already produced a receipt — so when the server does return a `client_secret`
 * instead, this is the half that was missing.
 */
export const confirmBankTransfer = async (
  stripe: Stripe,
  elements: StripeElements,
  clientSecret: string,
  details: BankTransferDetails,
): Promise<void> => {
  const bankAccount = elements.getElement('auBankAccount');
  if (bankAccount === null) {
    throw new PaymentError('The bank account form is not ready yet. Try again in a moment.');
  }

  const { error } = await stripe.confirmAuBecsDebitPayment(clientSecret, {
    payment_method: {
      au_becs_debit: bankAccount,
      billing_details: { name: details.name.trim(), email: details.email.trim() },
    },
  });

  if (error !== undefined) {
    throw new PaymentError(error.message ?? 'That payment could not be confirmed.');
  }
};

/**
 * Whether the charge still needs confirming in the browser.
 *
 * A receipt means the backend already took the money, and confirming again
 * would be a second charge. This is the check the legacy code added after that
 * exact bug — it is preserved rather than reasoned about afresh.
 */
export const needsClientConfirmation = (result: PaymentResult): boolean => {
  const hasReceipt =
    result.receipt?.fileURL !== null &&
    result.receipt?.fileURL !== undefined &&
    result.receipt.fileURL !== '';
  const secret = result.client_secret;
  return !hasReceipt && secret !== null && secret !== undefined && secret !== '';
};
